import { TimePicker } from "@tql/ui-components-react/kendo/dateinputs";
import { Input } from "@tql/ui-components-react/kendo/inputs";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { DatePicker } from "@tql/ui-components-react/tql/dateinputs";
import { useId, useState } from "react";
import { useParams } from "react-router";

import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";

import { usePostMarkLoaded } from "../../hooks/use-post-mark-loaded.ts";
import * as styles from "./mark-loaded-modal.module.css";

export const MarkLoadedModal = ({
	onClose,
	initialProNumber,
}: {
	onClose: () => void;
	initialProNumber?: string | null;
}) => {
	const loadedDateId = useId();
	const loadedTimeId = useId();
	const proNumberId = useId();
	const params = useParams();
	const { markLoaded, isSavingMarkLoaded } = usePostMarkLoaded();

	const [loadedDate, setLoadedDate] = useState<Date | null>(null);
	const [loadedTime, setLoadedTime] = useState<Date | null>(null);
	const [proNumber, setProNumber] = useState(initialProNumber ?? "");
	const [submitted, setSubmitted] = useState(false);

	// Combine the selected date and time into a single instant so it can be
	// validated against "now" and sent as a UTC ISO string.
	const completedTime =
		loadedDate && loadedTime
			? new Date(
					loadedDate.getFullYear(),
					loadedDate.getMonth(),
					loadedDate.getDate(),
					loadedTime.getHours(),
					loadedTime.getMinutes(),
					loadedTime.getSeconds(),
				)
			: null;
	const isFuture = completedTime ? completedTime.getTime() > Date.now() : false;

	const handleConfirm = () => {
		setSubmitted(true);
		if (!completedTime || isFuture) {
			return;
		}
		markLoaded({
			poNumber: params["poNumber"] ?? "",
			completedTime: completedTime.toISOString(),
			proNumber: proNumber.trim() || undefined,
		});
		onClose();
	};

	return (
		<TraxModal
			confirmText={"Confirm Loaded"}
			onCancel={onClose}
			onClose={onClose}
			onConfirm={handleConfirm}
			showButtonsDisabled={isSavingMarkLoaded}
			title={"LTL Loaded Details"}
			type={"confirmation"}
			width={303}
		>
			<div className={styles.form}>
				<div className={styles.row}>
					<div className={styles.field}>
						<Label editorId={loadedDateId}>
							Loaded date
							<span className={"required"}>&nbsp;*</span>
						</Label>
						<DatePicker
							id={loadedDateId}
							max={new Date()}
							onChange={(event) => setLoadedDate(event.target.value)}
							value={loadedDate}
						/>
						{submitted && !loadedDate ? (
							<Error>Loaded date is required.</Error>
						) : null}
					</div>
					<div className={styles.field}>
						<Label editorId={loadedTimeId}>
							Time
							<span className={"required"}>&nbsp;*</span>
						</Label>
						<TimePicker
							id={loadedTimeId}
							onChange={(event) => setLoadedTime(event.value)}
							value={loadedTime}
						/>
						{submitted && !loadedTime ? (
							<Error>Loaded time is required.</Error>
						) : null}
					</div>
				</div>

				{submitted && isFuture ? (
					<Error>Loaded date and time cannot be in the future.</Error>
				) : null}

				<div className={styles.field}>
					<Label editorId={proNumberId}>PRO Number</Label>
					<Input
						id={proNumberId}
						onChange={(event) => setProNumber(event.target.value as string)}
						value={proNumber}
					/>
				</div>
			</div>
		</TraxModal>
	);
};
